/**
 * Shopping Cart Management Utilities for WoodAlps E-commerce
 * 
 * This module provides comprehensive cart functionality including:
 * - Local storage persistence for cart state across sessions
 * - Item quantity management with validation and limits
 * - Cart calculations (totals, item counts, pricing)
 * - Cart validation and error handling for corrupted data
 * - Cross-browser compatibility with fallback handling
 * 
 * Key Features:
 * - Automatic cart data validation and cleanup
 * - Error handling for localStorage issues
 * - Cart item structure validation
 * - Performance optimized for frequent cart operations
 * - Support for custom product configurations
 * 
 * @description Complete cart management system with localStorage persistence and validation
 */

const CART_STORAGE_KEY = 'woodalps-cart';

/**
 * Retrieves the current cart from local storage
 * @returns {Array} Array of cart items
 */
export const getCartItems = () => {
  if (typeof window === 'undefined') return [];
  
  try {
    const cartData = localStorage.getItem(CART_STORAGE_KEY);
    return cartData ? JSON.parse(cartData) : [];
  } catch (error) {
    console.error('Error getting cart from localStorage:', error);
    return [];
  }
};

/**
 * Saves the cart to local storage
 * @param {Array} cartItems - Array of cart items to save
 */
export const saveCart = (cartItems) => {
  if (typeof window === 'undefined') return;
  
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    // Dispatch a custom event to notify components about cart changes
    window.dispatchEvent(new Event('cart-updated'));
  } catch (error) {
    console.error('Error saving cart to localStorage:', error);
  }
};

/**
 * Ensures a category value is properly formatted
 * @param {string} category - Category value to format
 * @returns {string} Properly formatted category
 */
const formatCategory = (category) => {
  if (!category) return '';
  
  // Handle common categories, ensuring proper capitalization
  const lowerCaseCategory = category.toLowerCase();
  if (lowerCaseCategory.includes('mtb')) return 'MTB';
  if (lowerCaseCategory.includes('road')) return 'Road';
  if (lowerCaseCategory.includes('gravel')) return 'Gravel';
  
  // For custom categories, capitalize first letter
  return category.charAt(0).toUpperCase() + category.slice(1).toLowerCase();
};

/**
 * Ensures product type is standardized
 * @param {string} productType - Product type to format
 * @returns {string} Standardized product type
 */
const formatProductType = (productType) => {
  if (!productType) return 'frame'; // Default to frame
  
  // Only allow 'bike' or 'frame' (lowercase)
  const lowerCaseType = productType.toLowerCase();
  return lowerCaseType.includes('bike') ? 'bike' : 'frame';
};

/**
 * Adds an item to the cart
 * @param {Object} product - Product to add to cart
 * @param {Object} options - Additional options like quantity, size, color, etc.
 * @returns {boolean} Success status
 */
export const addToCart = (product, options = {}) => {
  if (!product || !product.id) return false;
  
  try {
    const cart = getCartItems();
    
    // Check if this product is already in the cart
    const existingItemIndex = cart.findIndex(item => 
      item.id === product.id && 
      item.size === options.size && 
      item.color === options.color
    );
    
    if (existingItemIndex >= 0) {
      // Item exists, update quantity within limits
      const updatedCart = [...cart];
      const existingItem = updatedCart[existingItemIndex];
      
      // Check product type limits
      const newQuantity = existingItem.quantity + (options.quantity || 1);
      if (existingItem.productType === 'bike' && newQuantity > 1) {
        console.error('Maximum 1 unit allowed for bike products');
        return false;
      } else if (existingItem.productType === 'frame' && newQuantity > 3) {
        console.error('Maximum 3 units allowed for frame products');
        return false;
      }
      
      // Update quantity
      updatedCart[existingItemIndex] = {
        ...existingItem,
        quantity: newQuantity,
        // Ensure engraving status is preserved or updated if provided in options
        engraving: options.engraving !== undefined ? options.engraving : existingItem.engraving
      };
      
      saveCart(updatedCart);
    } else {
      // Item doesn't exist, add new item
      const newItem = {
        id: product.id,
        slug: product.slug?.current || product.slug || product.id, // Store the proper product slug for correct linking
        title: product.title,
        productType: formatProductType(product.productType),
        category: formatCategory(product.category),
        price: product.price || 0,
        quantity: options.quantity || 1,
        coverImage: product.coverImage || null,
        cardImages: product.cardImages || null,
        image: product.image || '',
        size: options.size || '',
        color: options.color || '',
        engraving: options.engraving || false,
        // Include the uniqueProductId to ensure different products have different images
        uniqueProductId: product.uniqueProductId || null
      };
      
      // Debug output
      console.log('Adding item to cart:', {
        id: newItem.id,
        title: newItem.title,
        productType: newItem.productType,
        category: newItem.category,
        coverImage: newItem.coverImage,
        hasCardImages: !!newItem.cardImages,
        cardImagesCount: newItem.cardImages ? newItem.cardImages.length : 0,
        quantity: newItem.quantity
      });
      
      // Check product type limits for new item
      if (newItem.productType === 'bike' && newItem.quantity > 1) {
        console.error('Maximum 1 unit allowed for bike products');
        return false;
      } else if (newItem.productType === 'frame' && newItem.quantity > 3) {
        console.error('Maximum 3 units allowed for frame products');
        return false;
      }
      
      saveCart([...cart, newItem]);
    }
    
    return true;
  } catch (error) {
    console.error('Error adding item to cart:', error);
    return false;
  }
};

/**
 * Updates the quantity of a cart item
 * @param {string|number} itemId - ID of the item to update
 * @param {number} quantity - New quantity
 * @param {Object} identifiers - Additional identifiers like size, color to uniquely identify the item
 * @returns {boolean} Success status
 */
export const updateCartItemQuantity = (itemId, quantity, identifiers = {}) => {
  if (!itemId || quantity < 1) return false;
  
  try {
    const cart = getCartItems();
    
    // Find the item to update
    const itemIndex = cart.findIndex(item => {
      if (item.id !== itemId) return false;
      
      // Check additional identifiers if provided
      if (identifiers.size && item.size !== identifiers.size) return false;
      if (identifiers.color && item.color !== identifiers.color) return false;
      
      return true;
    });
    
    if (itemIndex === -1) return false;
    
    const item = cart[itemIndex];
    
    // Check product type limits
    if (item.productType === 'bike' && quantity > 1) {
      console.error('Maximum 1 unit allowed for bike products');
      return false;
    } else if (item.productType === 'frame' && quantity > 3) {
      console.error('Maximum 3 units allowed for frame products');
      return false;
    }
    
    // Update the item
    const updatedCart = [...cart];
    updatedCart[itemIndex] = {
      ...item,
      quantity: quantity
    };
    
    saveCart(updatedCart);
    return true;
  } catch (error) {
    console.error('Error updating cart item quantity:', error);
    return false;
  }
};

/**
 * Removes an item from the cart
 * @param {string|number} itemId - ID of the item to remove
 * @param {Object} identifiers - Additional identifiers like size, color to uniquely identify the item
 * @returns {boolean} Success status
 */
export const removeCartItem = (itemId, identifiers = {}) => {
  if (!itemId) return false;
  
  try {
    const cart = getCartItems();
    
    // Filter out the item to remove
    const updatedCart = cart.filter(item => {
      if (item.id !== itemId) return true;
      
      // Check additional identifiers if provided
      if (identifiers.size && item.size !== identifiers.size) return true;
      if (identifiers.color && item.color !== identifiers.color) return true;
      
      return false; // Remove this item
    });
    
    saveCart(updatedCart);
    return true;
  } catch (error) {
    console.error('Error removing item from cart:', error);
    return false;
  }
};

/**
 * Clears the entire cart
 * @returns {boolean} Success status
 */
export const clearCart = () => {
  try {
    saveCart([]);
    return true;
  } catch (error) {
    console.error('Error clearing cart:', error);
    return false;
  }
};

/**
 * Calculates the total value of the cart
 * @returns {number} Total cart value
 */
export const getCartTotal = () => {
  try {
    const cart = getCartItems();
    return cart.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0);
  } catch (error) {
    console.error('Error calculating cart total:', error);
    return 0;
  }
};

/**
 * Gets the total number of items in the cart
 * @returns {number} Total number of items
 */
export const getCartCount = () => {
  try {
    const cart = getCartItems();
    return cart.length;
  } catch (error) {
    console.error('Error getting cart count:', error);
    return 0;
  }
}; 